import { Col, Pagination, Progress, Row, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { MdPictureAsPdf } from "react-icons/md";
import CustomProgressBar from "./CustomProgressBar";
import { useMediaQuery } from "react-responsive";
import type { ColumnType } from "antd/es/table/interface";
import moment from "moment";
import HealthScreeningReportModal from "./HealthScreeningReportModal";
import {
  AppDispatch,
  getMyOrganization,
  getReadingList,
  getReadingListRequest,
  Reading,
  useDynamicSelector } from
"@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { startCase } from "lodash";
import {
  countFormat,
  getAgeCalculate,
  getVitalColorCode,
  pluralize } from
"@src/helpers/constants";

type AlignType = "left" | "right" | "center"; // Define AlignType manually

interface ReadingsTableProps {}

interface DataSourceItem {
  key: string;
  worker: string;
  dateTime: string;
  pulseRate: number;
  skinTemp: string;
  spo2: number;
  rr: number;
  bp: {systolic: number;diastolic: number;} | string;
  sugar: number;
  bmi: number;
  ishScore: string;
}

const ReadingsTable: React.FC<ReadingsTableProps> = ({}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [openPdf, setOpenPdf] = useState<boolean>(false);
  const [readingItem, setReadingItem] = useState<any>({});
  const [columnsList, setColumnsList] = useState<any>([]);
  const [title, setTitle] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const { result: myOrganization, loading } = useSelector(getMyOrganization);
  const { result: readingList, loading: readingListLoading } =
  useSelector(getReadingList);
  const sidebarFilter = useDynamicSelector("reading_filter");

  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const onCancel = () => {
    setOpenPdf(false);
    setReadingItem({});
  };

  const path = location.pathname.split("/");
  const pageRouteName = path[1];

  const openReadingsPdf = (record: Reading) => {
    setOpenPdf(true);
    setReadingItem(record);
  };
  const handleViewDetails = (record: Reading) => {
    navigate(`/details/${record.subject?.id}`);
  };

  useEffect(() => {
    setTitle(
      pluralize(myOrganization?.rootOrganization?.subjectLabel || "")
    );
  }, [myOrganization]);

  useEffect(() => {
    const columns: any[] = [
    {
      title: title,
      key: "worker",
      align: "center",
      fixed: "left",
      width: 140,
      render: (record: Reading) => {
        const sortedValueList = [
        ...(record?.subject?.keyValueList || [])].
        sort(
          (a, b) =>
          (a?.organizationSubjectKey?.priorityOrder ?? 0) - (
          b?.organizationSubjectKey?.priorityOrder ?? 0)
        );

        const highestPriorityValue = sortedValueList?.[0]?.value;

        return (
          <Tooltip
            placement="bottom"
            overlayClassName="subject-info-tooltip"
            getPopupContainer={() => document.body}
            title={
            <div style={{ width: "200px" }}>
                  <a
                className="subject-user-name-container"
                href={`/details/${record.subject?.id}`}>

                    {record?.subject?.name}
                  </a>
                  <hr />
                  <span>{startCase(record?.subject?.subjectType)}</span>
                  <br />
                  <span>{startCase(record?.subject?.gender)}</span>
                  <br />
                  <span>
                    {getAgeCalculate(record?.subject?.dob?.toString() || "")}
                  </span>
                </div>}>

            
              <div className="worker-container">
                <span className="worker-table-name">
                  {record?.subject?.name}
                </span>
                <span>{highestPriorityValue}</span>
              </div>
            </Tooltip>);

      }
    },
    {
      title: "Date / Time",
      key: "datetime",
      align: "center" as AlignType,
      fixed: "left",
      width: 105,
      render: (record: Reading) => {
        return moment(record?.datetime).format("DD-MM-YYYY hh:mm A");
      }
    },
    {
      title: "Pulse Rate",
      key: "pulseRate",
      align: "center" as AlignType,
      render: (record: Reading) =>
      <CustomProgressBar
        record={{
          value: record?.vincenseReading?.pulseValue,
          color: record?.vincenseReading?.pulseColor
        }} />


    },
    {
      title: "Skin Temp",
      key: "skinTemp",
      align: "center" as AlignType,
      render: (record: Reading) =>
      record?.vincenseReading?.skinValue ?
      <CustomProgressBar
        record={{
          value: record?.vincenseReading?.skinValue
        }} /> :


      "-"

    },
    {
      title: "SpO2",
      key: "spo2",
      align: "center" as AlignType,
      render: (record: Reading) =>
      <CustomProgressBar
        record={{
          value: record?.vincenseReading?.o2Value,
          color: record?.vincenseReading?.o2Color
        }} />


    },
    {
      title: "RR",
      key: "rr",
      align: "center" as AlignType,
      render: (record: Reading) =>
      <CustomProgressBar
        record={{
          value: record?.vincenseReading?.rrValue,
          color: record?.vincenseReading?.rrColor
        }} />


    },
    {
      title: "BP",
      key: "bp",
      align: "center" as AlignType,
      render: (record: Reading) =>
      record.bpReading?.diastolicValue &&
      record.bpReading?.systolicValue ?
      <div className="bp-progress-container">
              <div className="bp-value">
                <span>{record.bpReading?.systolicValue}</span>
                <Progress
            percent={100}
            showInfo={false}
            strokeColor={getVitalColorCode(record.bpReading?.systolicColor)}
            className="custom-progress" />
          
              </div>
              <span>/</span>
              <div className="bp-value">
                <span>{record.bpReading?.diastolicValue}</span>
                <Progress
            percent={100}
            showInfo={false}
            strokeColor={getVitalColorCode(record.bpReading?.diastolicColor)}
            className="custom-progress" />
          
              </div>
            </div> :

      "-"

    },
    {
      title: "Sugar",
      key: "sugar",
      align: "center" as AlignType,
      render: (record: Reading) =>
      record?.glucometerReading?.randomValue ?
      <CustomProgressBar
        record={{
          value: record?.glucometerReading?.randomValue,
          color: record?.glucometerReading?.randomColor
        }} /> :

      record?.glucometerReading?.fastingValue ||
      record?.glucometerReading?.ppValue ?
      <div className="bp-progress-container">
              <div className="bp-value">
                <span>{record?.glucometerReading?.fastingValue || "-"}</span>
                <Progress
            percent={100}
            showInfo={false}
            strokeColor={getVitalColorCode(
            record?.glucometerReading?.fastingColor)}

            className="custom-progress" />
          
              </div>
              <span>/</span>
              <div className="bp-value">
                <span>{record?.glucometerReading?.ppValue || "-"}</span>
                <Progress
            percent={100}
            showInfo={false}
            strokeColor={getVitalColorCode(record.glucometerReading?.ppColor)}
            className="custom-progress" />
          
              </div>
            </div> :

      "-"

    },
    {
      title: "BMI",
      key: "bmi",
      align: "center" as AlignType,
      render: (record: Reading) =>
      <CustomProgressBar
        record={{
          value: record?.weighingReading?.bmiValue,
          color: record?.weighingReading?.bmiColor
        }} />


    },
    {
      title: "ISH Score",
      key: "ishScore",
      align: "center" as AlignType,
      render: (record: Reading) =>
      record?.ishReading?.score && record?.ishReading?.color ?
      <CustomProgressBar
        record={{
          value: record?.ishReading?.score,
          color: record?.ishReading?.color
        }} /> :


      "-"

    },
    {
      title: "Action",
      key: "action",
      align: "center" as AlignType,
      width: 68,
      // fixed: "right",
      render: (record: Reading) =>
      <MdPictureAsPdf
        onClick={() => openReadingsPdf(record)}
        className={
        record?.ecgReading?.ecgUrl || record?.ecgReading?.pdfUrl ? "pdf-icon-ecg" : "pdf-icon"}

        size={25} />


    }];


    if (id) {
      setColumnsList(columns.filter((list) => list.key !== "worker"));
    } else {
      setColumnsList(columns);
    }
  }, [id, myOrganization, title]);

  const handleChange = (pageNumber: number, pageLimit: number) => {
    dispatch(
      getReadingListRequest({
        filter: sidebarFilter?.filter,
        pagination: {
          pageLimit: pageLimit,
          pageNumber: pageNumber
        },
        sort: [
        {
          field: "datetime",
          order: "desc"
        }]

      })
    );
  };

  useEffect(() => {
    if (id) {
      const requestPayload: {
        filter: {subjectId: string;};
        sort: {field: string;order: "desc" | "asc";}[];
        pagination?: {pageLimit: number;pageNumber: number;};
      } = {
        filter: { subjectId: id || "" },
        sort: [
        {
          field: "datetime",
          order: "desc"
        }]

      };

      if (pageRouteName !== "details") {
        requestPayload.pagination = {
          pageLimit: 10,
          pageNumber: 1
        };
      }

      dispatch(getReadingListRequest(requestPayload));
    }
  }, [id, pageRouteName]);

  const ecgReadings = readingList?.items?.map((reading) => ({
    id: reading.ecgReading?.id,
    ecgUrl: reading.ecgReading?.ecgUrl,
    pdfUrl: reading.ecgReading?.pdfUrl
  }));

  return (
    <div>
      <Table
        className="readings-table"
        dataSource={readingList?.items || []}
        columns={columnsList}
        pagination={false}
        loading={readingListLoading || loading}
        bordered
        scroll={{
          x: isMobile ? 1000 : undefined,
          y: "calc(100vh - 220px)"
        }}
        style={{ borderTop: "0px" }}
        rowClassName={(_, index) => index % 2 === 0 ? "even-row" : "odd-row"}
        rowHoverable={false} />
      

      {pageRouteName !== "details" &&
      <Row justify={"end"} style={{ marginTop: "10px" }}>
          <Col>
            <Pagination
            onChange={handleChange}
            current={readingList?.pagination?.pageNumber || 0}
            pageSize={readingList?.pagination?.pageLimit || 0}
            total={readingList?.pagination?.totalCount || 0} />
          
          </Col>
        </Row>}
      
      <HealthScreeningReportModal
        visible={openPdf}
        onCancel={onCancel}
        record={readingItem} />
      
    </div>);

};

export default ReadingsTable;